import { PrivateWaypoint, WaypointCategory, GeoCoordinate } from '../types';

interface GPXWaypoint {
  lat: number;
  lon: number;
  name?: string;
  desc?: string;
  ele?: number;
  time?: string;
  sym?: string;
  type?: string;
}

export class GPXParser {
  static async parseGPXFile(gpxContent: string): Promise<Omit<PrivateWaypoint, 'id' | 'userId' | 'createdAt' | 'updatedAt'>[]> {
    try {
      const waypoints = this.extractWaypointsFromXML(gpxContent);
      return waypoints.map(wp => this.convertToPrivateWaypoint(wp));
    } catch (error) {
      throw new Error(`Failed to parse GPX file: ${  (error as Error).message}`);
    }
  }

  private static extractWaypointsFromXML(xmlContent: string): GPXWaypoint[] {
    const waypoints: GPXWaypoint[] = [];
    
    // Extract waypoints using regex
    const wptRegex = /<wpt\s+lat="([^"]+)"\s+lon="([^"]+)"[^>]*>([\s\S]*?)<\/wpt>/g;
    let match;
    
    while ((match = wptRegex.exec(xmlContent)) !== null) {
      const waypoint: GPXWaypoint = {
        lat: parseFloat(match[1]),
        lon: parseFloat(match[2])
      };
      
      const wptContent = match[3];
      
      // Extract name
      const nameMatch = /<name[^>]*>(.*?)<\/name>/s.exec(wptContent);
      if (nameMatch) waypoint.name = this.decodeXmlEntities(nameMatch[1].trim());
      
      // Extract description
      const descMatch = /<desc[^>]*>(.*?)<\/desc>/s.exec(wptContent);
      if (descMatch) waypoint.desc = this.decodeXmlEntities(descMatch[1].trim());
      
      // Extract elevation
      const eleMatch = /<ele[^>]*>(.*?)<\/ele>/s.exec(wptContent);
      if (eleMatch) waypoint.ele = parseFloat(eleMatch[1]);
      
      // Extract time
      const timeMatch = /<time[^>]*>(.*?)<\/time>/s.exec(wptContent);
      if (timeMatch) waypoint.time = timeMatch[1].trim();
      
      // Extract symbol
      const symMatch = /<sym[^>]*>(.*?)<\/sym>/s.exec(wptContent);
      if (symMatch) waypoint.sym = this.decodeXmlEntities(symMatch[1].trim());
      
      // Extract type
      const typeMatch = /<type[^>]*>(.*?)<\/type>/s.exec(wptContent);
      if (typeMatch) waypoint.type = this.decodeXmlEntities(typeMatch[1].trim());
      
      waypoints.push(waypoint);
    }
    
    // Also extract route points
    const rteRegex = /<rte[^>]*>([\s\S]*?)<\/rte>/g;
    let rteMatch;
    
    while ((rteMatch = rteRegex.exec(xmlContent)) !== null) {
      const rteContent = rteMatch[1];
      const rteptRegex = /<rtept\s+lat="([^"]+)"\s+lon="([^"]+)"[^>]*>([\s\S]*?)<\/rtept>/g;
      let rteptMatch;
      let pointIndex = 0;
      
      while ((rteptMatch = rteptRegex.exec(rteContent)) !== null) {
        const waypoint: GPXWaypoint = {
          lat: parseFloat(rteptMatch[1]),
          lon: parseFloat(rteptMatch[2])
        };
        
        const rteptContent = rteptMatch[3];
        
        // Extract name
        const nameMatch = /<name[^>]*>(.*?)<\/name>/s.exec(rteptContent);
        waypoint.name = nameMatch ? this.decodeXmlEntities(nameMatch[1].trim()) : `Route Point ${++pointIndex}`;
        
        // Extract description
        const descMatch = /<desc[^>]*>(.*?)<\/desc>/s.exec(rteptContent);
        if (descMatch) waypoint.desc = this.decodeXmlEntities(descMatch[1].trim());
        
        // Extract elevation
        const eleMatch = /<ele[^>]*>(.*?)<\/ele>/s.exec(rteptContent);
        if (eleMatch) waypoint.ele = parseFloat(eleMatch[1]);
        
        waypoints.push(waypoint);
      }
    }
    
    return waypoints;
  }

  private static decodeXmlEntities(text: string): string {
    return text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(parseInt(dec)))
      .replace(/&#x([a-fA-F0-9]+);/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)));
  }

  private static convertToPrivateWaypoint(gpxWaypoint: GPXWaypoint): Omit<PrivateWaypoint, 'id' | 'userId' | 'createdAt' | 'updatedAt'> {
    const coordinates: GeoCoordinate = {
      latitude: gpxWaypoint.lat,
      longitude: gpxWaypoint.lon,
    };

    const category = this.determineCategory(gpxWaypoint);
    const name = gpxWaypoint.name || `Waypoint ${gpxWaypoint.lat.toFixed(4)}, ${gpxWaypoint.lon.toFixed(4)}`;

    const waypoint: Omit<PrivateWaypoint, 'id' | 'userId' | 'createdAt' | 'updatedAt'> = {
      name,
      coordinates,
      category,
      isVisible: true,
    };

    if (gpxWaypoint.desc) {
      waypoint.description = gpxWaypoint.desc;
    }

    // Add metadata if available
    const metadata: any = {};
    if (gpxWaypoint.ele) {
      metadata.depth = gpxWaypoint.ele * -1; // Convert elevation to depth (assuming negative values for underwater)
    }
    
    if (Object.keys(metadata).length > 0) {
      waypoint.metadata = metadata;
    }

    // Extract tags from type or symbol
    const tags: string[] = [];
    if (gpxWaypoint.type) {
      tags.push(...gpxWaypoint.type.split(',').map(t => t.trim()));
    }
    if (gpxWaypoint.sym) {
      tags.push(gpxWaypoint.sym);
    }
    if (tags.length > 0) {
      waypoint.tags = tags;
    }

    return waypoint;
  }

  private static determineCategory(gpxWaypoint: GPXWaypoint): WaypointCategory {
    const name = (gpxWaypoint.name || '').toLowerCase();
    const desc = (gpxWaypoint.desc || '').toLowerCase();
    const type = (gpxWaypoint.type || '').toLowerCase();
    const sym = (gpxWaypoint.sym || '').toLowerCase();

    const combined = `${name} ${desc} ${type} ${sym}`;

    if (combined.includes('reef')) return 'reef';
    if (combined.includes('wreck') || combined.includes('ship')) return 'wreck';
    if (combined.includes('structure') || combined.includes('rig') || combined.includes('platform')) return 'structure';
    if (combined.includes('anchor') || combined.includes('anchorage')) return 'anchorage';
    if (combined.includes('hazard') || combined.includes('danger') || combined.includes('warning')) return 'hazard';
    if (combined.includes('fish') || combined.includes('spot')) return 'fishing_spot';
    
    return 'personal';
  }

  static generateGPX(waypoints: PrivateWaypoint[]): string {
    const gpxHeader = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Ocean Insight Pro"
  xmlns="http://www.topografix.com/GPX/1/1"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>Ocean Insight Pro Waypoints</name>
    <desc>Private waypoints exported from Ocean Insight Pro</desc>
    <time>${new Date().toISOString()}</time>
  </metadata>`;

    const gpxFooter = '\n</gpx>';

    const waypointElements = waypoints.map(wp => {
      let wptXml = `\n  <wpt lat="${wp.coordinates.latitude}" lon="${wp.coordinates.longitude}">`;
      wptXml += `\n    <name>${this.escapeXml(wp.name)}</name>`;
      
      if (wp.description) {
        wptXml += `\n    <desc>${this.escapeXml(wp.description)}</desc>`;
      }
      
      if (wp.metadata?.depth) {
        wptXml += `\n    <ele>${-wp.metadata.depth}</ele>`; // Convert depth to negative elevation
      }
      
      wptXml += `\n    <type>${wp.category}</type>`;
      
      if (wp.tags && wp.tags.length > 0) {
        wptXml += `\n    <sym>${wp.tags[0]}</sym>`;
      }
      
      wptXml += '\n  </wpt>';
      return wptXml;
    }).join('');

    return gpxHeader + waypointElements + gpxFooter;
  }

  private static escapeXml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }
}